/**
 * When to come back to this company (R6).
 *
 * The ring before this gave the operator the words for the call. This is what
 * happens after it: "they are re-tendering in March", "ring back Tuesday",
 * "their new manager starts in three weeks". Without somewhere to put that,
 * every warm answer that was not a yes decays into nothing, because the only
 * record of it is in one person's head.
 *
 * QUICK BUTTONS FIRST, DATE FIELD SECOND. The three answers that come out of a
 * real call are "next week", "next month" and "next quarter", and a date picker
 * makes an operator who just put the phone down do arithmetic. The exact field
 * stays for the case the buttons cannot express, which is the one where
 * somebody said an actual date.
 *
 * ⚠ THE NOTE IS NOT A DROPDOWN, and that is deliberate. "Waiting for their new
 * manager to start" is the whole reason, and no enum this feature could invent
 * would have held it.
 */
import { useState } from "react";
import { CalendarClock, Check, X } from "lucide-react";
import { Button, DatePicker } from "@wa/ui";
import { useI18n } from "../../../../i18n/I18nProvider";
import { setBrainPlan, type BrainPlan } from "../../../../lib/siteBrain";
import { StatusPill, useToast } from "../../../ui";
import type { T } from "../../site-scanner/types";

/** The answers a call actually produces, in days. */
const QUICK: { key: string; days: number }[] = [
  { key: "week", days: 7 },
  { key: "month", days: 30 },
  { key: "quarter", days: 90 },
];

export function BrainPlanBox({
  domain,
  plan,
  canManage,
  onChanged,
  t,
}: {
  domain: string;
  plan: BrainPlan | null;
  canManage: boolean;
  onChanged: () => void;
  t: T;
}) {
  const toast = useToast();
  const { locale } = useI18n();
  const [note, setNote] = useState(plan?.note ?? "");
  const [date, setDate] = useState(plan ? plan.nextTouchAt.slice(0, 10) : "");
  const [saving, setSaving] = useState(false);

  async function save(at: string | null) {
    if (saving) return;
    setSaving(true);
    try {
      await setBrainPlan(domain, { nextTouchAt: at, note: at ? note.trim() || null : null });
      toast(t(at ? "brain.plan.saved" : "brain.plan.cleared"));
      onChanged();
    } catch {
      toast(t("brain.plan.failed"));
    } finally {
      setSaving(false);
    }
  }

  /** Midnight `days` out, so "next week" is a day rather than a moment. */
  const inDays = (days: number): string => {
    const at = new Date();
    at.setHours(9, 0, 0, 0);
    at.setDate(at.getDate() + days);
    return at.toISOString();
  };

  if (!canManage) {
    // Read-only still SHOWS the plan. Somebody without the permission to change
    // a follow-up still needs to know one exists before they ring the company.
    return plan ? <PlanPill plan={plan} t={t} /> : null;
  }

  return (
    <section className="space-y-2 rounded-2xl bg-[var(--crm-sunk)] px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <CalendarClock className="size-4 shrink-0 text-muted-foreground" />
        <span className="text-sm font-semibold">{t("brain.plan.title")}</span>
        {plan ? <PlanPill plan={plan} t={t} /> : null}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {QUICK.map((q) => (
          <Button key={q.key} variant="outline" size="sm" disabled={saving} onClick={() => void save(inDays(q.days))}>
            {t(`brain.plan.in.${q.key}`)}
          </Button>
        ))}

        {/* The shared calendar rather than `<input type="date">`. The repo's own
            note on `[data-slot="datepicker-trigger"]` says why: the native one
            is a different control in every browser, six screens had each dragged
            it back to the CRM's pill by hand, and two more shipped the kit's
            white box next to sunken ones. */}
        <span className="min-w-[9rem]">
          <DatePicker value={date} onChange={setDate} locale={locale} placeholder={t("brain.plan.dateLabel")} />
        </span>
        <Button
          variant="ghost"
          size="sm"
          // A date field with nothing in it must not clear an existing plan:
          // that is the button next to it, and the two must never be one.
          disabled={saving || !date}
          onClick={() => void save(new Date(`${date}T09:00:00`).toISOString())}
        >
          <Check className="size-3.5" />
          {t("brain.plan.set")}
        </Button>

        {plan ? (
          <Button variant="ghost" size="sm" disabled={saving} onClick={() => void save(null)}>
            <X className="size-3.5" />
            {t("brain.plan.clear")}
          </Button>
        ) : null}
      </div>

      {/* `crm-inp` is the CRM's own field dress and it carries its own height -
          see the note on the class. A bare input here rendered the kit's white
          box next to sunken ones, which is the exact drift that class exists to
          end. */}
      <label className="crm-inp">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={400}
          placeholder={t("brain.plan.notePlaceholder")}
          aria-label={t("brain.plan.noteLabel")}
        />
      </label>
    </section>
  );
}

/** The date, and whether it has passed. */
function PlanPill({ plan, t }: { plan: BrainPlan; t: T }) {
  const day = plan.nextTouchAt.slice(0, 10);
  return (
    <>
      <StatusPill tone={plan.due ? "wt" : "mut"}>
        {plan.due
          ? t("brain.plan.due", { date: day })
          : t("brain.plan.inDays", { n: plan.inDays, date: day })}
      </StatusPill>
      {plan.note ? <span className="crm-mut crm-trunc text-xs">{plan.note}</span> : null}
    </>
  );
}
